import {
	AnalysisGroup,
	AnalysisSetting,
	AnalysisSettingsValue,
	CalculationBaseGroup,
	ConstructiveSales,
	DataTypes,
	Dividend,
	HedgingType,
	Hedging,
	MTM,
	MTMSubSetting,
} from './Settings.types';

export class CalculationSetting {
	buildWashSalesAnalysisSettingValue = (
		washSalesAnalysis: string[],
		analysisSetting: AnalysisSetting[],
		overrideWashSalesFactors: boolean,
	): string => {
		const analysisSubSettingValues: AnalysisSettingsValue[] = [
			{
				analysisSettingId: analysisSetting.filter((i) => i.analysisGroup === AnalysisGroup.WashSales)[0]
					.analysisSettingID,
				settingValueType: DataTypes.Boolean,
				settingValues: [`${overrideWashSalesFactors}`],
			},
		];

		return this.buildSettings(washSalesAnalysis, analysisSubSettingValues);
	};

	buildConstructiveSalesAnalysisSettingValue = (
		analysis: string[],
		analysisSetting: AnalysisSetting[],
		reliefMethodology: string,
		forcedConstructiveSales: string,
	): string => {
		const analysisSettingID = analysisSetting.filter((i) => i.analysisGroup === AnalysisGroup.ConstructiveSales)[0]
			.analysisSettingID;
		const analysisSubSettingValues: AnalysisSettingsValue[] = [
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${reliefMethodology}`],
				type: ConstructiveSales.ReliefMethodology,
			},
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${forcedConstructiveSales}`],
				type: ConstructiveSales.ForcedConstructiveSales,
			},
		];

		return this.buildSettings(analysis, analysisSubSettingValues);
	};

	buildDividendAnalysisSettingValue = (
		dividendAnalysis: string[],
		analysisSetting: AnalysisSetting[],
		overrideDividendFactors: boolean,
		accrualBasis: string,
		cashBasisAdjustment: boolean
	): string => {
		const analysisId = analysisSetting.filter((i) => i.analysisGroup === AnalysisGroup.Dividend);
		if(analysisId.length === 0){
			return '';
		}
		const analysisSubSettingValues: AnalysisSettingsValue[] = [
			{
				analysisSettingId: analysisId[0].analysisSettingID,
				settingValueType: DataTypes.Boolean,
				settingValues: [`${overrideDividendFactors}`],
				type: Dividend.OverridDividendFactors,
			},
			{
				analysisSettingId: analysisId[0].analysisSettingID,
				settingValueType: DataTypes.Boolean,
				settingValues: [`${cashBasisAdjustment}`],
				type: Dividend.CashBasisAdjustment,
			}
		];

		return this.buildSettings(dividendAnalysis, analysisSubSettingValues);
	};

	buildAnalysisSettingValue = (
		analysisGroup: AnalysisGroup,
		analysis: string[],
		analysisSetting: AnalysisSetting[],
		value: string,
	): string => {
		const analysisSubSettingValues: AnalysisSettingsValue[] = [
			{
				analysisSettingId: analysisSetting
					.filter((i) => i.analysisGroup === analysisGroup)[0]
					.analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${value}`],
			},
		];

		return this.buildSettings(analysis, analysisSubSettingValues);
	};

	buildHedgingAnalysisSettingValue = (
		analysis: string[],
		analysisSetting: AnalysisSetting[],
		hedgingSetting: string,
		offsetRelief: string,
		realizedRelief: string,
		sameDayHedging: boolean
	): string => {
		const analysisSettingID = analysisSetting.filter((i) => i.analysisGroup === AnalysisGroup.Hedging)[0]
			.analysisSettingID;
		const analysisSubSettingValues: AnalysisSettingsValue[] = [
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${hedgingSetting}`],
				type: Hedging.HedgingType,
			},
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${offsetRelief}`],
				type: Hedging.OffsetRelief,
			},
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.String,
				settingValues: [`${realizedRelief}`],
				type: Hedging.RealizedRelief,
			},
			{
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.Boolean,
				settingValues: [`${sameDayHedging}`],
				type: Hedging.SameDayHedging,
			}
		];
		return this.buildSettings(analysis, analysisSubSettingValues);
	};

	buildMTMAnalysisSettingValue = (
		mtmAnalysis: string[],
		analysisSetting: AnalysisSetting[],
		mtmSubSettings : MTMSubSetting[]
	): string => {
		const analysisSettingID = analysisSetting.filter((i) => i.analysisGroup === AnalysisGroup.MTM)[0]
			.analysisSettingID;
		const analysisSubSettingValues: AnalysisSettingsValue[] = [];
		mtmSubSettings.forEach(mtmSetting => {
			const analysisSubSettingValue: AnalysisSettingsValue = {
				analysisSettingId: analysisSettingID,
				settingValueType: DataTypes.Boolean,
				settingValues: [`${mtmSetting.value}`],
		 		type: Number(mtmSetting.name) as MTM
			}
			analysisSubSettingValues.push(analysisSubSettingValue);
		});
		return this.buildSettings(mtmAnalysis, analysisSubSettingValues);
	};

	buildNonSubSettingsAnalysisSettingValue = (nonSubSettingsAnalysis: string[]): string => {
		return this.buildSettings(nonSubSettingsAnalysis, []);
	};

	buildCalculationAnalysis = (
		clientType: string,
		periodStartDate: Date,
		periodEndDate: Date,
		reporting: string,
		bifurcation: string,
		character: string
	): string => {
		let analysisSubSettingValues: AnalysisSettingsValue[] = [];
		const calculationSettingID = 'baseCalcSettings';

		analysisSubSettingValues.push(
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.PeriodFrom,
				DataTypes.Date,
				[periodStartDate.toLocaleString()],
				calculationSettingID,
			),
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.PeriodTo,
				DataTypes.Date,
				[periodEndDate.toLocaleString()],
				calculationSettingID,
			),
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.ClientType,
				DataTypes.String,
				[`${clientType}`],
				calculationSettingID,
			),
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.Reporting,
				DataTypes.String,
				[`${reporting}`],
				calculationSettingID,
			),
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.Bifurcation,
				DataTypes.String,
				[`${bifurcation}`],
				calculationSettingID,
			),
			this.buildAnalysisSettingValueCalculation(
				CalculationBaseGroup.Character,
				DataTypes.String,
				[`${character}`],
				calculationSettingID,
			),
		);
		return JSON.stringify({ analysisSetting: analysisSubSettingValues });
	};

	buildSettings = (analysis: string[], analysisSubSettingValues: AnalysisSettingsValue[]): string => {
		return JSON.stringify({
			analysis: analysis,
			analysisSetting: analysisSubSettingValues,
		});
	};

	buildAnalysisSettingValueCalculation = (
		calculationGroupId: CalculationBaseGroup,
		settingValueType: string,
		settingValues: string[],
		analysisSettingID: string,
	): AnalysisSettingsValue => {
		return {
			type: calculationGroupId,
			analysisSettingId: analysisSettingID,
			settingValueType: settingValueType,
			settingValues: settingValues,
		};
	};
}
